import axios from "axios";
import {
  AlertTriangle,
  Edit,
  LogOut,
  Package,
  Plus,
  RefreshCw,
  Save,
  ShoppingCart,
  Store,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const API_URL = "https://bgimanagement.my.id/api";
const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
];

function App() {
  // Authentication State
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");

  // UI State
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [loading, setLoading] = useState(false);

  // Data State
  const [inventory, setInventory] = useState([]);
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [mySales, setMySales] = useState([]);
  const [myStats, setMyStats] = useState({
    totalSales: 0,
    totalTransactions: 0,
  });

  // Inventory Form State (REVISI: Hapus field status karena otomatis)
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    branch: "karangnunggal",
    fruit_name: "",
    stock: "",
    unit: "kg",
    price: "",
    expiry_date: "",
    // TIDAK ADA status lagi, karena otomatis dari backend
  });

  // Sales Form State
  const [showSalesForm, setShowSalesForm] = useState(false);
  const [salesForm, setSalesForm] = useState({
    branch: "karangnunggal",
    sale_date: new Date().toISOString().split("T")[0],
    items: [],
    notes: "",
    discount_type: "rupiah", // BARU
    discount_value: 0, // BARU
    shipping_cost: 0, // BARU
  });
  const [selectedItems, setSelectedItems] = useState([]);

  // Date Filter State
  const [salesDateFilter, setSalesDateFilter] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  // Receipt State
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  // API Configuration
  const api = axios.create({ baseURL: API_URL });

  api.interceptors.request.use(
    (config) => {
      const currentToken = localStorage.getItem("token");
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Effects
  useEffect(() => {
    if (token) {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) setUser(storedUser);
    }
  }, [token]);

  useEffect(() => {
    if (user) fetchAllData();
  }, [user]);

  // AUTO-REFRESH DINONAKTIFKAN - Gunakan tombol refresh manual
  // useEffect(() => {
  //   if (user) {
  //     const interval = setInterval(() => fetchAllData(), 30000);
  //     return () => clearInterval(interval);
  //   }
  // }, [user]);

  // Auto-fetch saat filter tanggal berubah
  useEffect(() => {
    if (user && activeTab === "sales") {
      fetchAllData();
    }
  }, [salesDateFilter]);
  // Formatting Functions
  const formatRupiah = (num) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "urgent":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "urgent":
        return "Segera Dijual";
      case "warning":
        return "Perlu Perhatian";
      default:
        return "Baik";
    }
  };

  // Data Processing Functions
  const getAlertItems = () => {
    return inventory.filter(
      (item) =>
        (item.status === "urgent" || item.status === "warning") &&
        item.stock > 0,
    );
  };

  const getFilteredInventory = () => {
    return selectedBranch === "all"
      ? inventory
      : inventory.filter((item) => item.branch === selectedBranch);
  };

  const getComparisonData = () => {
    const branches = ["karangnunggal", "tasikkota", "manonjaya"];
    return branches.map((branch) => {
      const branchInventory = inventory.filter((i) => i.branch === branch);
      const totalStock = branchInventory.reduce((sum, i) => sum + i.stock, 0);
      const branchSale = stats?.branchSales?.find((s) => s.branch === branch);
      return {
        name:
          branch.charAt(0).toUpperCase() +
          branch.slice(1).replace("kota", " Kota"),
        stok: totalStock,
        penjualan: Math.round((branchSale?.today || 0) / 1000),
      };
    });
  };

  const getStockByCategory = () => {
    const categoryStock = {};
    inventory.forEach((item) => {
      if (!categoryStock[item.fruit_name]) {
        categoryStock[item.fruit_name] = 0;
      }
      categoryStock[item.fruit_name] += item.stock;
    });
    return Object.keys(categoryStock).map((name) => ({
      name,
      value: categoryStock[name],
    }));
  };
  // Function untuk total stok keseluruhan per item - Admin Pusat
  const getTotalStockPerItem = () => {
    const stockMap = {};

    inventory.forEach((item) => {
      const key = `${item.fruit_name}-${item.unit}`;

      if (!stockMap[key]) {
        stockMap[key] = {
          fruit_name: item.fruit_name,
          unit: item.unit,
          total_stock: 0,
          branches: [],
        };
      }

      stockMap[key].total_stock += parseFloat(item.stock) || 0;
      stockMap[key].branches.push({
        branch: item.branch,
        stock: item.stock,
      });
    });

    return Object.values(stockMap).sort((a, b) =>
      a.fruit_name.localeCompare(b.fruit_name),
    );
  };

  const calculateTotalSales = () => {
    const subtotal = selectedItems.reduce(
      (sum, item) => sum + (item.subtotal || 0),
      0,
    );

    // Hitung diskon
    let discountAmount = 0;
    if (salesForm.discount_type === "percent") {
      discountAmount =
        (subtotal * parseFloat(salesForm.discount_value || 0)) / 100;
    } else {
      discountAmount = parseFloat(salesForm.discount_value || 0);
    }

    // Hitung total akhir
    const shippingCost = parseFloat(salesForm.shipping_cost || 0);
    const finalTotal = subtotal - discountAmount + shippingCost;

    return {
      subtotal,
      discountAmount,
      shippingCost,
      finalTotal: finalTotal > 0 ? finalTotal : 0,
    };
  };
  // Fetch All Data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      if (user.role === "kasir") {
        const [inventoryRes, mySalesRes, myStatsRes] = await Promise.all([
          api.get("/inventory"),
          api.get("/sales/my-sales"),
          api.get("/sales/my-stats"),
        ]);
        setInventory(inventoryRes.data);
        setMySales(mySalesRes.data);
        setMyStats(myStatsRes.data);
      } else {
        const [inventoryRes, salesRes, statsRes] = await Promise.all([
          api.get("/inventory"),
          api.get(
            `/sales?startDate=${salesDateFilter.startDate}&endDate=${salesDateFilter.endDate}`,
          ),
          api.get("/dashboard/stats"),
        ]);
        setInventory(inventoryRes.data);
        setSales(salesRes.data);
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  // Authentication Handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const response = await axios.post(`${API_URL}/login`, loginForm);
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      setToken(token);
      setUser(user);
      api.defaults.headers.Authorization = `Bearer ${token}`;
      if (user.role === "kasir") {
        setActiveTab("sales");
      }
    } catch (error) {
      setLoginError(error.response?.data?.message || "Login gagal");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
    setInventory([]);
    setSales([]);
    setStats(null);
    setActiveTab("dashboard");

    // RESET state kasir
    setMySales([]);
    setMyStats({ totalSales: 0, totalTransactions: 0 });

    // RESET receipt (PENTING!)
    setReceiptData(null);
    setShowReceipt(false);
  };
  // Inventory Management
  const resetItemForm = () => {
    setItemForm({
      branch: user?.role === "cabang" ? user.branch : "karangnunggal",
      fruit_name: "",
      stock: "",
      unit: "kg",
      price: "",
      expiry_date: "",
      // TIDAK ADA status, karena otomatis
    });
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      // TIDAK PERLU kirim status, backend yang handle otomatis
      if (editingItem) {
        await api.put(`/inventory/${editingItem.id}`, itemForm);
      } else {
        await api.post("/inventory", itemForm);
      }
      fetchAllData();
      setShowAddForm(false);
      setEditingItem(null);
      resetItemForm();
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || "Gagal menyimpan"));
    }
  };

  const handleDeleteItem = async (id) => {
    const item = inventory.find((inv) => inv.id === id);
    const itemName = item ? item.fruit_name : "Item";

    if (
      window.confirm(
        `⚠️ PERHATIAN!\n\n` +
          `Yakin ingin MENGHAPUS "${itemName}"?\n\n` +
          `⚠️ PERINGATAN:\n` +
          `• Data akan dihapus PERMANEN\n` +
          `• Riwayat penjualan item ini juga akan TERHAPUS\n` +
          `• Tindakan ini TIDAK DAPAT DIBATALKAN`,
      )
    ) {
      try {
        const response = await api.delete(`/inventory/${id}`);
        await fetchAllData();

        const deletedCount = response.data.deletedSalesItems || 0;
        alert(
          `✅ Item "${itemName}" berhasil dihapus!\n` +
            (deletedCount > 0
              ? `\n📊 ${deletedCount} riwayat penjualan juga telah dihapus.`
              : ""),
        );
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Gagal menghapus item";
        alert("❌ " + errorMessage);
      }
    }
  };

  const startEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      branch: item.branch,
      fruit_name: item.fruit_name,
      stock: item.stock,
      unit: item.unit,
      price: item.price,
      expiry_date: item.expiry_date,
      // TIDAK ADA status
    });
    setShowAddForm(true);
  };
  // Sales Management
  const handleAddItemToSale = () => {
    const newItem = {
      inventory_id: "",
      fruit_name: "",
      quantity: "",
      unit: "kg",
      price: "",
      subtotal: 0,
    };
    setSelectedItems([...selectedItems, newItem]);
  };

  const handleRemoveItemFromSale = (index) => {
    const newItems = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...selectedItems];
    newItems[index][field] = value;

    if (field === "inventory_id") {
      const selectedInventory = inventory.find(
        (inv) => inv.id === parseInt(value),
      );
      if (selectedInventory) {
        newItems[index].fruit_name = selectedInventory.fruit_name;
        newItems[index].unit = selectedInventory.unit;
        newItems[index].price = selectedInventory.price;
        newItems[index].max_stock = selectedInventory.stock;
      }
    }

    if (field === "quantity" || field === "price") {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].price) || 0;
      newItems[index].subtotal = qty * price;
    }

    setSelectedItems(newItems);
  };

  const handleAddSales = async (e) => {
    e.preventDefault();

    if (selectedItems.length === 0) {
      alert("Tambahkan minimal 1 item untuk dijual");
      return;
    }

    const invalidItems = selectedItems.filter(
      (item) => !item.inventory_id || !item.quantity || item.quantity <= 0,
    );

    if (invalidItems.length > 0) {
      alert("Pastikan semua item telah diisi dengan benar");
      return;
    }

    const overStockItems = selectedItems.filter(
      (item) => parseInt(item.quantity) > parseInt(item.max_stock),
    );

    if (overStockItems.length > 0) {
      alert("Jumlah penjualan melebihi stok yang tersedia!");
      return;
    }

    try {
      const branchToUse =
        user?.role === "cabang" || user?.role === "kasir"
          ? user.branch
          : salesForm.branch;

      const totals = calculateTotalSales();

      const response = await api.post("/sales", {
        branch: branchToUse,
        sale_date: salesForm.sale_date,
        items: selectedItems,
        notes: salesForm.notes,
        discount_type: salesForm.discount_type, // BARU
        discount_value: salesForm.discount_value, // BARU
        discount_amount: totals.discountAmount, // BARU
        shipping_cost: salesForm.shipping_cost, // BARU
        subtotal: totals.subtotal, // BARU
        total_amount: totals.finalTotal, // UPDATE
      });

      fetchAllData();
      setShowSalesForm(false);
      setSelectedItems([]);
      setSalesForm({
        branch:
          user?.role === "cabang" || user?.role === "kasir"
            ? user.branch
            : "karangnunggal",
        sale_date: new Date().toISOString().split("T")[0],
        items: [],
        notes: "",
        discount_type: "rupiah", // RESET
        discount_value: 0, // RESET
        shipping_cost: 0, // RESET
      });
      alert("Penjualan berhasil ditambahkan dan stok telah dikurangi!");

      if (user.role === "kasir" && response.data.id) {
        handlePrintReceipt(response.data.id);
      }
    } catch (error) {
      alert(
        "Error: " +
          (error.response?.data?.message || "Gagal menambah penjualan"),
      );
    }
  };

  const handlePrintReceipt = async (saleId) => {
    try {
      const response = await api.get(`/sales/${saleId}/receipt`);
      setReceiptData(response.data);
      setShowReceipt(true);

      setTimeout(() => {
        window.print();
      }, 500);
    } catch (error) {
      alert(
        "Error: " +
          (error.response?.data?.message || "Gagal mengambil data struk"),
      );
    }
  };

  // Date Filter Functions
  const handleDateFilterChange = (field, value) => {
    setSalesDateFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const applyDateFilter = () => {
    fetchAllData();
  };

  const resetDateFilter = () => {
    const today = new Date().toISOString().split("T")[0];
    setSalesDateFilter({
      startDate: today,
      endDate: today,
    });
  };

  const setFilterPeriod = (period) => {
    const today = new Date();
    const startDate = new Date();

    switch (period) {
      case "today":
        startDate.setDate(today.getDate());
        break;
      case "yesterday":
        startDate.setDate(today.getDate() - 1);
        today.setDate(today.getDate() - 1);
        break;
      case "week":
        startDate.setDate(today.getDate() - 7);
        break;
      case "month":
        startDate.setMonth(today.getMonth() - 1);
        break;
      default:
        break;
    }

    setSalesDateFilter({
      startDate: startDate.toISOString().split("T")[0],
      endDate: today.toISOString().split("T")[0],
    });
  };

  // Receipt Component
  // ========== GANTI COMPONENT Receipt di App.js ==========
  // Cari: const Receipt = ({ data }) => {
  // Ganti dengan kode ini:

  const Receipt = ({ data }) => {
    if (!data) return null;

    return (
      <>
        <style>{`
        @media print {
          @page {
            size: 80mm auto;
            margin: 0;
          }
          body * {
            visibility: hidden;
          }
          .print-receipt,
          .print-receipt * {
            visibility: visible;
          }
          .print-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
          }
        }
        
        .print-receipt {
          display: ${showReceipt ? "block" : "none"};
          max-width: 80mm;
          margin: 0 auto;
          padding: 5mm;
          font-family: 'Courier New', monospace;
          font-size: 9pt;
          line-height: 1.4;
          background: white;
        }
        
        .receipt-wavy-top {
          border-top: 3px dashed #000;
          margin-bottom: 3mm;
        }
        
        .receipt-header {
          text-align: center;
          margin-bottom: 3mm;
        }
        
        .receipt-title {
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 2mm;
        }
        
        .receipt-address {
          font-size: 9pt;
          margin-bottom: 1mm;
        }
        
        .receipt-divider {
          border-top: 1px dashed #000;
          margin: 3mm 0;
        }
        
        .receipt-info {
          font-size: 9pt;
          margin: 1mm 0;
        }
        
        .receipt-flex {
          display: flex;
          justify-content: space-between;
          margin: 1mm 0;
        }
        
        .receipt-items {
          margin: 3mm 0;
        }
        
        .receipt-item {
          margin: 2mm 0;
        }
        
        .receipt-item-line {
          display: flex;
          justify-content: space-between;
          font-size: 9pt;
        }
        
        .receipt-subtotal {
          margin: 3mm 0;
          padding: 2mm 0;
          border-top: 1px dashed #000;
        }
        
        .receipt-total {
          font-weight: bold;
          font-size: 14pt;
          margin: 2mm 0;
          padding-top: 2mm;
          border-top: 2px solid #000;
        }
        
        .receipt-footer {
          text-align: center;
          margin-top: 5mm;
          font-size: 8pt;
        }
        
        .receipt-wavy-bottom {
          border-bottom: 3px dashed #000;
          margin-top: 3mm;
        }
      `}</style>

        <div className="print-receipt">
          <div className="receipt-wavy-top"></div>

          {/* Header */}
          <div className="receipt-header">
            <div className="receipt-title">PT Bahagia Grup Indonesia</div>
            <div
              className="receipt-address"
              style={{ textTransform: "capitalize" }}
            >
              Cabang {data.sale.branch.replace("kota", " Kota")}
            </div>
          </div>

          <div className="receipt-divider"></div>

          {/* Info Transaksi */}
          <div className="receipt-info">
            No.{String(data.sale.id).padStart(6, "0")}
          </div>
          <div className="receipt-flex">
            <span>{formatDate(data.sale.sale_date)}</span>
            <span>Kasir: {data.sale.cashier_name || data.sale.username}</span>
          </div>
          <div className="receipt-flex">
            <span>{new Date().toLocaleTimeString("id-ID")}</span>
            <span></span>
          </div>

          <div className="receipt-divider"></div>

          {/* Items */}
          <div className="receipt-items">
            {data.items.map((item, idx) => (
              <div key={idx} className="receipt-item">
                <div style={{ fontWeight: "bold" }}>{item.fruit_name}</div>
                <div className="receipt-item-line">
                  <span>
                    {item.quantity} x {formatRupiah(item.price)}
                  </span>
                  <span>{formatRupiah(item.subtotal)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="receipt-divider"></div>

          {/* Subtotal & Breakdown */}
          <div className="receipt-subtotal">
            {/* Subtotal */}
            <div className="receipt-flex">
              <span>Subtotal</span>
              <span>
                {formatRupiah(data.sale.subtotal || data.sale.total_amount)}
              </span>
            </div>

            {/* Diskon - SELALU TAMPIL */}
            <div className="receipt-flex">
              <span>
                Diskon
                {data.sale.discount_type === "percent" &&
                data.sale.discount_value > 0
                  ? ` (${data.sale.discount_value}%)`
                  : ""}
              </span>
              <span>
                {data.sale.discount_amount > 0 ? "- " : ""}
                {formatRupiah(data.sale.discount_amount || 0)}
              </span>
            </div>

            {/* Ongkir - SELALU TAMPIL */}
            <div className="receipt-flex">
              <span>Ongkir</span>
              <span>{formatRupiah(data.sale.shipping_cost || 0)}</span>
            </div>
          </div>

          {/* Total Akhir */}
          <div className="receipt-flex receipt-total">
            <span>TOTAL</span>
            <span>{formatRupiah(data.sale.total_amount)}</span>
          </div>

          <div className="receipt-divider"></div>

          {/* Catatan */}
          {data.sale.notes && (
            <>
              <div className="receipt-info">Catatan: {data.sale.notes}</div>
              <div className="receipt-divider"></div>
            </>
          )}

          {/* Footer */}
          <div className="receipt-footer">
            <div style={{ marginBottom: "2mm" }}>
              Terima kasih atas kunjungan Anda
            </div>
            <div>Barang yang sudah dibeli</div>
            <div>tidak dapat dikembalikan</div>
          </div>

          <div className="receipt-wavy-bottom"></div>
        </div>
      </>
    );
  };
  // Login Page
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              PT Bahagia Grup Indonesia
            </h1>
            <p className="text-gray-600">Sistem Monitoring Cabang</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) =>
                  setLoginForm({ ...loginForm, username: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) =>
                  setLoginForm({ ...loginForm, password: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {loginError && (
              <div className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }
  // Kasir Dashboard
  if (user.role === "kasir") {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-6">
        <Receipt data={receiptData} />

        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  🏪 PT Bahagia Grup Indonesia
                </h1>
                <p className="text-gray-600 mt-1">
                  Selamat datang, {user.name} (Kasir - {user.branch})
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={fetchAllData}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs - REVISI: Kasir hanya punya 2 tab */}
          <div className="bg-white rounded-lg shadow-md p-2 mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveTab("sales")}
                className={`flex-1 md:flex-none px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === "sales"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                💰 Penjualan
              </button>
              <button
                onClick={() => setActiveTab("stock")}
                className={`flex-1 md:flex-none px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === "stock"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                📦 Stok Tersedia
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-600">Loading...</div>
            </div>
          ) : (
            <>
              {/* Sales Tab */}
              {activeTab === "sales" && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm">
                            Penjualan Saya Hari Ini
                          </p>
                          <p className="text-3xl font-bold mt-1">
                            {formatRupiah(myStats.totalSales)}
                          </p>
                        </div>
                        <TrendingUp className="w-12 h-12 text-green-200" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">
                            Total Transaksi Hari Ini
                          </p>
                          <p className="text-3xl font-bold mt-1">
                            {myStats.totalTransactions}
                          </p>
                        </div>
                        <ShoppingCart className="w-12 h-12 text-blue-200" />
                      </div>
                    </div>
                  </div>

                  {/* Add Sales Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        setShowSalesForm(true);
                        setSelectedItems([]);
                      }}
                      className="flex items-center gap-3 px-8 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 shadow-lg text-lg font-semibold transition"
                    >
                      <Plus className="w-6 h-6" />
                      Tambah Penjualan Baru
                    </button>
                  </div>
                  {/* Sales Form */}
                  {showSalesForm && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold mb-4">
                        Tambah Penjualan Baru
                      </h3>
                      <form onSubmit={handleAddSales} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tanggal
                          </label>
                          <input
                            type="date"
                            value={salesForm.sale_date}
                            onChange={(e) =>
                              setSalesForm({
                                ...salesForm,
                                sale_date: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>

                        {/* Items List */}
                        <div className="border-t pt-4">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium">
                              Daftar Item Penjualan
                            </h4>
                            <button
                              type="button"
                              onClick={handleAddItemToSale}
                              className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm transition"
                            >
                              <Plus className="w-4 h-4" />
                              Tambah Item
                            </button>
                          </div>

                          {selectedItems.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                              <ShoppingCart className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                              <p className="text-gray-500">
                                Belum ada item. Klik "Tambah Item" untuk memulai
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {selectedItems.map((item, index) => (
                                <div
                                  key={index}
                                  className="bg-gray-50 p-4 rounded-lg"
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                    <div className="md:col-span-2">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Buah
                                      </label>
                                      <select
                                        value={item.inventory_id}
                                        onChange={(e) =>
                                          handleItemChange(
                                            index,
                                            "inventory_id",
                                            e.target.value,
                                          )
                                        }
                                        className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                        required
                                      >
                                        <option value="">Pilih Buah</option>
                                        {inventory
                                          .filter(
                                            (inv) => inv.branch === user.branch,
                                          )
                                          .map((inv) => (
                                            <option key={inv.id} value={inv.id}>
                                              {inv.fruit_name} (Stok:{" "}
                                              {inv.stock} {inv.unit})
                                            </option>
                                          ))}
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Jumlah
                                      </label>
                                      <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) =>
                                          handleItemChange(
                                            index,
                                            "quantity",
                                            e.target.value,
                                          )
                                        }
                                        className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                        required
                                      />
                                      {item.max_stock && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Max: {item.max_stock}
                                        </p>
                                      )}
                                    </div>

                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Harga
                                      </label>
                                      <input
                                        type="number"
                                        value={item.price}
                                        onChange={(e) =>
                                          handleItemChange(
                                            index,
                                            "price",
                                            e.target.value,
                                          )
                                        }
                                        className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                        required
                                      />
                                    </div>

                                    <div className="flex items-end">
                                      <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Subtotal
                                        </label>
                                        <div className="px-3 py-2 bg-white border rounded text-sm font-semibold">
                                          {formatRupiah(item.subtotal)}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleRemoveItemFromSale(index)
                                        }
                                        className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded transition"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Total dengan Breakdown - GANTI YANG LAMA DENGAN INI */}
                        {selectedItems.length > 0 && (
                          <div className="space-y-3">
                            {/* Diskon Section */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <label className="block text-sm font-semibold text-gray-700 mb-3">
                                💰 Diskon (Optional)
                              </label>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">
                                    Tipe Diskon
                                  </label>
                                  <select
                                    value={salesForm.discount_type}
                                    onChange={(e) =>
                                      setSalesForm({
                                        ...salesForm,
                                        discount_type: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="rupiah">Rupiah (Rp)</option>
                                    <option value="percent">Persen (%)</option>
                                  </select>
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-xs text-gray-600 mb-1">
                                    Nilai Diskon{" "}
                                    {salesForm.discount_type === "percent"
                                      ? "(%)"
                                      : "(Rp)"}
                                  </label>
                                  <input
                                    type="number"
                                    value={salesForm.discount_value}
                                    onChange={(e) =>
                                      setSalesForm({
                                        ...salesForm,
                                        discount_value: e.target.value,
                                      })
                                    }
                                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                    placeholder={
                                      salesForm.discount_type === "percent"
                                        ? "Contoh: 10"
                                        : "Contoh: 50000"
                                    }
                                    min="0"
                                    max={
                                      salesForm.discount_type === "percent"
                                        ? "100"
                                        : undefined
                                    }
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Ongkir Section */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <label className="block text-sm font-semibold text-gray-700 mb-3">
                                🚚 Biaya Pengiriman (Optional)
                              </label>
                              <input
                                type="number"
                                value={salesForm.shipping_cost}
                                onChange={(e) =>
                                  setSalesForm({
                                    ...salesForm,
                                    shipping_cost: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                placeholder="Contoh: 15000"
                                min="0"
                              />
                            </div>

                            {/* Total Breakdown */}
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                              <div className="space-y-2">
                                {/* Subtotal */}
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-700">
                                    Subtotal:
                                  </span>
                                  <span className="font-semibold text-gray-900">
                                    {formatRupiah(
                                      calculateTotalSales().subtotal,
                                    )}
                                  </span>
                                </div>

                                {/* Diskon */}
                                {calculateTotalSales().discountAmount > 0 && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-red-600">
                                      Diskon{" "}
                                      {salesForm.discount_type === "percent"
                                        ? `(${salesForm.discount_value}%)`
                                        : ""}
                                      :
                                    </span>
                                    <span className="font-semibold text-red-600">
                                      -{" "}
                                      {formatRupiah(
                                        calculateTotalSales().discountAmount,
                                      )}
                                    </span>
                                  </div>
                                )}

                                {/* Ongkir */}
                                {calculateTotalSales().shippingCost > 0 && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">
                                      Ongkir:
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                      +{" "}
                                      {formatRupiah(
                                        calculateTotalSales().shippingCost,
                                      )}
                                    </span>
                                  </div>
                                )}

                                {/* Divider */}
                                <div className="border-t border-blue-300 my-2"></div>

                                {/* Total Akhir */}
                                <div className="flex justify-between items-center">
                                  <span className="text-lg font-bold text-gray-800">
                                    Total Bayar:
                                  </span>
                                  <span className="text-2xl font-bold text-blue-600">
                                    {formatRupiah(
                                      calculateTotalSales().finalTotal,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Catatan (Optional)
                          </label>
                          <textarea
                            value={salesForm.notes}
                            onChange={(e) =>
                              setSalesForm({
                                ...salesForm,
                                notes: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            rows="2"
                            placeholder="Tambahkan catatan jika diperlukan"
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-4 border-t">
                          <button
                            type="submit"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                          >
                            <Save className="w-4 h-4" />
                            Simpan Penjualan
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowSalesForm(false);
                              setSelectedItems([]);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                          >
                            <X className="w-4 h-4" />
                            Batal
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* My Transactions */}
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      📋 Transaksi Saya Hari Ini
                    </h3>
                    {mySales.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        Belum ada transaksi hari ini
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Waktu
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Total
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Item
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Catatan
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Aksi
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {mySales.map((sale) => (
                              <tr key={sale.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {sale.sale_time || "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap font-semibold text-green-600">
                                  {formatRupiah(sale.total_amount)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {sale.item_count} item
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {sale.notes || "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    onClick={() => handlePrintReceipt(sale.id)}
                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium transition"
                                  >
                                    🖨️ Print Struk
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stock Tab - Kasir hanya bisa lihat */}
              {activeTab === "stock" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      📦 Stok Tersedia - Cabang {user.branch}
                    </h3>
                    <span className="text-sm text-gray-500">
                      Total: {inventory.length} item
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Nama Buah
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Stok
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Harga
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Hari di Gudang
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {inventory.length === 0 ? (
                          <tr>
                            <td
                              colSpan="5"
                              className="px-4 py-8 text-center text-gray-500"
                            >
                              Belum ada stok buah
                            </td>
                          </tr>
                        ) : (
                          inventory.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">
                                {item.fruit_name}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`font-semibold ${
                                    item.stock > 10
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {item.stock} {item.unit}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-900">
                                {formatRupiah(item.price)}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {item.days_in_warehouse} hari
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(
                                    item.status,
                                  )}`}
                                >
                                  {getStatusText(item.status)}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
  // Admin & Cabang Dashboard
  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                PT Bahagia Grup Indonesia
              </h1>
              <p className="text-gray-600 mt-1">
                Selamat datang, {user.name} (
                {user.role === "pusat"
                  ? "Admin Pusat"
                  : `Cabang ${user.branch}`}
                )
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchAllData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs - REVISI: Cabang ada tab Laporan Stok, bukan Manajemen Stok */}
        <div className="bg-white rounded-lg shadow-md p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`flex-1 md:flex-none px-6 py-3 rounded-lg font-medium transition ${
                activeTab === "dashboard"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Dashboard
            </button>

            {/* Tab Manajemen Stok hanya untuk Admin Pusat */}
            {user.role === "pusat" && (
              <button
                onClick={() => setActiveTab("stock")}
                className={`flex-1 md:flex-none px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === "stock"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Manajemen Stok
              </button>
            )}

            <button
              onClick={() => setActiveTab("sales")}
              className={`flex-1 md:flex-none px-6 py-3 rounded-lg font-medium transition ${
                activeTab === "sales"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Penjualan
            </button>

            {/* REVISI: Tab Laporan Stok untuk Cabang (bukan Manajemen) */}
            {user.role === "cabang" && (
              <button
                onClick={() => setActiveTab("reports")}
                className={`flex-1 md:flex-none px-6 py-3 rounded-lg font-medium transition ${
                  activeTab === "reports"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Laporan Stok
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Loading...</div>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === "dashboard" && stats && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {user.role === "pusat" && (
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">Total Cabang</p>
                          <p className="text-3xl font-bold mt-1">
                            {stats.totalBranches}
                          </p>
                        </div>
                        <Store className="w-12 h-12 text-blue-200" />
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">
                          Penjualan Hari Ini
                        </p>
                        <p className="text-3xl font-bold mt-1">
                          {formatRupiah(stats.todaySales)}
                        </p>
                      </div>
                      <TrendingUp className="w-12 h-12 text-green-200" />
                    </div>
                  </div>

                  {/* REVISI: Total Stok dipecah per satuan */}
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-purple-100 text-sm">Total Stok</p>
                      </div>
                      <Package className="w-12 h-12 text-purple-200" />
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div className="bg-purple-400 bg-opacity-30 rounded p-2">
                        <p className="text-xs text-purple-100">Kg</p>
                        <p className="text-lg font-bold">
                          {stats.totalStockKg || 0}
                        </p>
                      </div>
                      <div className="bg-purple-400 bg-opacity-30 rounded p-2">
                        <p className="text-xs text-purple-100">Dus</p>
                        <p className="text-lg font-bold">
                          {stats.totalStockDus || 0}
                        </p>
                      </div>
                      <div className="bg-purple-400 bg-opacity-30 rounded p-2">
                        <p className="text-xs text-purple-100">Krat</p>
                        <p className="text-lg font-bold">
                          {stats.totalStockKrat || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">
                          Item Perlu Perhatian
                        </p>
                        <p className="text-3xl font-bold mt-1">
                          {stats.alertItems}
                        </p>
                      </div>
                      <AlertTriangle className="w-12 h-12 text-orange-200" />
                    </div>
                  </div>
                </div>

                {/* Charts - Admin Pusat */}
                {user.role === "pusat" ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold mb-4">
                        Perbandingan Stok Antar Cabang
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={getComparisonData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip formatter={(value) => `${value} kg`} />
                          <Legend />
                          <Bar
                            dataKey="stok"
                            fill="#3b82f6"
                            name="Total Stok (kg)"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold mb-4">
                        Performa Penjualan per Cabang
                      </h3>
                      <div className="space-y-4">
                        {stats.branchSales?.map((branch, idx) => (
                          <div key={idx} className="border-b pb-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium capitalize">
                                {branch.branch.replace("kota", " Kota")}
                              </span>
                              <span className="text-sm text-gray-600">
                                Hari ini: {formatRupiah(branch.today)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <span>
                                Kemarin: {formatRupiah(branch.yesterday)}
                              </span>
                              <span>•</span>
                              <span>
                                Rata-rata:{" "}
                                {formatRupiah(Math.round(branch.weekAvg))}
                              </span>
                            </div>
                            <div className="mt-2">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full transition-all"
                                  style={{
                                    width: `${Math.min((branch.today / 3000000) * 100, 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Charts - Cabang
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg shadow-md p-6">
                        <h3 className="text-lg font-semibold mb-4">
                          Komposisi Stok Buah
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={getStockByCategory()}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, value }) =>
                                `${name}: ${value} kg`
                              }
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {getStockByCategory().map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="bg-white rounded-lg shadow-md p-6">
                        <h3 className="text-lg font-semibold mb-4">
                          Performa Penjualan Cabang Anda
                        </h3>
                        <div className="space-y-4">
                          {stats.branchSales?.map((branch, idx) => (
                            <div key={idx}>
                              <div className="mb-4">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium">Hari Ini</span>
                                  <span className="text-2xl font-bold text-green-600">
                                    {formatRupiah(branch.today)}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                  <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs text-gray-500">
                                      Kemarin
                                    </p>
                                    <p className="text-lg font-semibold">
                                      {formatRupiah(branch.yesterday)}
                                    </p>
                                  </div>
                                  <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs text-gray-500">
                                      Rata-rata 7 Hari
                                    </p>
                                    <p className="text-lg font-semibold">
                                      {formatRupiah(Math.round(branch.weekAvg))}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {stats.totalItems !== null && (
                                <div className="bg-blue-50 p-4 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-blue-900">
                                      Total Jenis Buah
                                    </span>
                                    <span className="text-2xl font-bold text-blue-600">
                                      {stats.totalItems}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Detail Stock Table for Cabang */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Package className="w-5 h-5 text-blue-600" />
                          Detail Stok Buah Cabang Anda
                        </h3>
                        <span className="text-sm text-gray-500">
                          Total: {getFilteredInventory().length} item
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Nama Buah
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Stok Tersedia
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Harga
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Hari di Gudang
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {getFilteredInventory().length === 0 ? (
                              <tr>
                                <td
                                  colSpan="5"
                                  className="px-4 py-8 text-center text-gray-500"
                                >
                                  Belum ada stok buah di cabang Anda
                                </td>
                              </tr>
                            ) : (
                              getFilteredInventory().map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                                    {item.fruit_name}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="font-semibold text-blue-600">
                                      {item.stock} {item.unit}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                                    {formatRupiah(item.price)}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                                    {item.days_in_warehouse} hari
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(
                                        item.status,
                                      )}`}
                                    >
                                      {getStatusText(item.status)}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alert Items */}
                {getAlertItems().length > 0 && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Item yang Memerlukan Perhatian
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getAlertItems().map((item) => (
                        <div
                          key={item.id}
                          className="border rounded-lg p-4 bg-orange-50"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold">{item.fruit_name}</h4>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                                item.status,
                              )}`}
                            >
                              {getStatusText(item.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 capitalize">
                            Cabang: {item.branch}
                          </p>
                          <p className="text-sm text-gray-600">
                            Stok: {item.stock} {item.unit}
                          </p>
                          <p className="text-sm text-gray-600">
                            Di gudang: {item.days_in_warehouse} hari
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Total Stok Keseluruhan - Admin Pusat */}
                {user.role === "pusat" && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      📦 Total Stok Keseluruhan per Item
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Nama Buah
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total Stok
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Satuan
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Detail per Cabang
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getTotalStockPerItem().map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {item.fruit_name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-blue-600">
                                  {item.total_stock}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {item.unit}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-500 space-y-1">
                                  {item.branches.map((branch, branchIdx) => (
                                    <div
                                      key={branchIdx}
                                      className="flex items-center gap-2"
                                    >
                                      <span className="font-medium capitalize">
                                        {branch.branch.replace("kota", " Kota")}
                                        :
                                      </span>
                                      <span>
                                        {branch.stock} {item.unit}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}

                          {getTotalStockPerItem().length === 0 && (
                            <tr>
                              <td
                                colSpan="4"
                                className="px-6 py-4 text-center text-sm text-gray-500"
                              >
                                Belum ada data stok
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Stock Management Tab - HANYA untuk Admin Pusat */}
            {activeTab === "stock" && user.role === "pusat" && (
              <div className="space-y-4">
                {/* Filter & Add Button */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedBranch("all")}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        selectedBranch === "all"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      Semua Cabang
                    </button>
                    {["karangnunggal", "tasikkota", "manonjaya"].map(
                      (branch) => (
                        <button
                          key={branch}
                          onClick={() => setSelectedBranch(branch)}
                          className={`px-4 py-2 rounded-lg font-medium capitalize transition ${
                            selectedBranch === branch
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                        >
                          {branch.replace("kota", " Kota")}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    onClick={() => {
                      resetItemForm();
                      setShowAddForm(true);
                      setEditingItem(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    <Plus className="w-4 h-4" />
                    Tambah/Restok Buah
                  </button>
                </div>

                {/* Add/Edit Form - REVISI: Tanpa field Status */}
                {showAddForm && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      {editingItem ? "Edit Stok" : "Tambah / Restok Buah"}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {editingItem
                        ? "Ubah informasi stok buah"
                        : "Masukkan buah baru atau tambah stok buah yang sudah ada"}
                    </p>
                    <form
                      onSubmit={handleSaveItem}
                      className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cabang
                        </label>
                        <select
                          value={itemForm.branch}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, branch: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="karangnunggal">Karangnunggal</option>
                          <option value="tasikkota">Tasik Kota</option>
                          <option value="manonjaya">Manonjaya</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nama Buah
                        </label>
                        <input
                          type="text"
                          value={itemForm.fruit_name}
                          onChange={(e) =>
                            setItemForm({
                              ...itemForm,
                              fruit_name: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Contoh: Apel Fuji"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Jumlah Stok {editingItem ? "(Update)" : "(Tambah)"}
                        </label>
                        <input
                          type="number"
                          value={itemForm.stock}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, stock: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="100"
                          required
                        />
                        {!editingItem && (
                          <p className="text-xs text-gray-500 mt-1">
                            Stok akan ditambahkan ke buah yang sudah ada
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Satuan
                        </label>
                        <select
                          value={itemForm.unit}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, unit: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="kg">Kg (Kilogram)</option>
                          <option value="dus">Dus</option>
                          <option value="krat">Krat</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Harga per Satuan (Rp)
                        </label>
                        <input
                          type="number"
                          value={itemForm.price}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, price: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="35000"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tanggal Kadaluarsa
                        </label>
                        <input
                          type="date"
                          value={itemForm.expiry_date}
                          onChange={(e) =>
                            setItemForm({
                              ...itemForm,
                              expiry_date: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* REVISI: TIDAK ADA field Status lagi, otomatis dari backend */}

                      <div className="md:col-span-2 flex gap-2">
                        <button
                          type="submit"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                          <Save className="w-4 h-4" />
                          Simpan
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddForm(false);
                            setEditingItem(null);
                            resetItemForm();
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                        >
                          <X className="w-4 h-4" />
                          Batal
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Inventory Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Buah
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Cabang
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Stok
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Harga
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Kadaluarsa
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Hari di Gudang
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Aksi
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getFilteredInventory().length === 0 ? (
                          <tr>
                            <td
                              colSpan="8"
                              className="px-6 py-8 text-center text-gray-500"
                            >
                              Belum ada data stok
                            </td>
                          </tr>
                        ) : (
                          getFilteredInventory().map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                {item.fruit_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-600 capitalize">
                                {item.branch.replace("kota", " Kota")}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-semibold">
                                {item.stock} {item.unit}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                {formatRupiah(item.price)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                {formatDate(item.expiry_date)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                {item.days_in_warehouse} hari
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(
                                    item.status,
                                  )}`}
                                >
                                  {getStatusText(item.status)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => startEditItem(item)}
                                    className="text-blue-600 hover:text-blue-800 transition"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="text-red-600 hover:text-red-800 transition"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {/* Sales Tab */}
            {activeTab === "sales" && (
              <div className="space-y-4">
                {/* Header dengan Filter Tanggal */}
                <div className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex flex-wrap items-end gap-4">
                    {/* Quick Filter Buttons */}
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filter Cepat
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setFilterPeriod("today")}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                        >
                          Hari Ini
                        </button>
                        <button
                          onClick={() => setFilterPeriod("yesterday")}
                          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                        >
                          Kemarin
                        </button>
                        <button
                          onClick={() => setFilterPeriod("week")}
                          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                        >
                          7 Hari
                        </button>
                        <button
                          onClick={() => setFilterPeriod("month")}
                          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                        >
                          30 Hari
                        </button>
                      </div>
                    </div>

                    {/* Date Range Picker */}
                    <div className="flex gap-2 items-end flex-wrap">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dari Tanggal
                        </label>
                        <input
                          type="date"
                          value={salesDateFilter.startDate}
                          onChange={(e) =>
                            handleDateFilterChange("startDate", e.target.value)
                          }
                          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Sampai Tanggal
                        </label>
                        <input
                          type="date"
                          value={salesDateFilter.endDate}
                          onChange={(e) =>
                            handleDateFilterChange("endDate", e.target.value)
                          }
                          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <button
                        onClick={applyDateFilter}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        Terapkan
                      </button>

                      <button
                        onClick={resetDateFilter}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                      >
                        Reset
                      </button>
                    </div>

                    {/* Add Sales Button */}
                    <button
                      onClick={() => {
                        setShowSalesForm(true);
                        setSelectedItems([]);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Tambah Penjualan
                    </button>
                  </div>

                  {/* Info Period */}
                  <div className="mt-3 text-sm text-gray-600">
                    📅 Menampilkan data:{" "}
                    <strong>{formatDate(salesDateFilter.startDate)}</strong> s/d{" "}
                    <strong>{formatDate(salesDateFilter.endDate)}</strong>
                    {sales.length > 0 && (
                      <span className="ml-2">
                        | Total:{" "}
                        <strong className="text-green-600">
                          {formatRupiah(
                            sales.reduce((sum, s) => sum + s.total_amount, 0),
                          )}
                        </strong>{" "}
                        dari <strong>{sales.length}</strong> transaksi
                      </span>
                    )}
                  </div>
                </div>

                {/* Sales Form */}
                {showSalesForm && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Tambah Penjualan Baru
                    </h3>
                    <form onSubmit={handleAddSales} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {user.role === "pusat" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Cabang
                            </label>
                            <select
                              value={salesForm.branch}
                              onChange={(e) =>
                                setSalesForm({
                                  ...salesForm,
                                  branch: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              required
                            >
                              <option value="karangnunggal">
                                Karangnunggal
                              </option>
                              <option value="tasikkota">Tasik Kota</option>
                              <option value="manonjaya">Manonjaya</option>
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tanggal
                          </label>
                          <input
                            type="date"
                            value={salesForm.sale_date}
                            onChange={(e) =>
                              setSalesForm({
                                ...salesForm,
                                sale_date: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      </div>

                      {/* Items List */}
                      <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium">Daftar Item Penjualan</h4>
                          <button
                            type="button"
                            onClick={handleAddItemToSale}
                            className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm transition"
                          >
                            <Plus className="w-4 h-4" />
                            Tambah Item
                          </button>
                        </div>

                        {selectedItems.length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <ShoppingCart className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                            <p className="text-gray-500">
                              Belum ada item. Klik "Tambah Item" untuk memulai
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {selectedItems.map((item, index) => (
                              <div
                                key={index}
                                className="bg-gray-50 p-4 rounded-lg"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Buah
                                    </label>
                                    <select
                                      value={item.inventory_id}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "inventory_id",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                      required
                                    >
                                      <option value="">Pilih Buah</option>
                                      {inventory
                                        .filter((inv) =>
                                          user.role === "cabang"
                                            ? inv.branch === user.branch
                                            : inv.branch === salesForm.branch,
                                        )
                                        .map((inv) => (
                                          <option key={inv.id} value={inv.id}>
                                            {inv.fruit_name} (Stok: {inv.stock}{" "}
                                            {inv.unit})
                                          </option>
                                        ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Jumlah
                                    </label>
                                    <input
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "quantity",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                      placeholder="0"
                                      required
                                    />
                                    {item.max_stock && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Max: {item.max_stock}
                                      </p>
                                    )}
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Harga
                                    </label>
                                    <input
                                      type="number"
                                      value={item.price}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "price",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                                      placeholder="0"
                                      required
                                    />
                                  </div>

                                  <div className="flex items-end">
                                    <div className="flex-1">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Subtotal
                                      </label>
                                      <div className="px-3 py-2 bg-white border rounded text-sm font-semibold">
                                        {formatRupiah(item.subtotal)}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveItemFromSale(index)
                                      }
                                      className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded transition"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Total dengan Breakdown - GANTI YANG LAMA DENGAN INI */}
                      {selectedItems.length > 0 && (
                        <div className="space-y-3">
                          {/* Diskon Section */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                              💰 Diskon (Optional)
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">
                                  Tipe Diskon
                                </label>
                                <select
                                  value={salesForm.discount_type}
                                  onChange={(e) =>
                                    setSalesForm({
                                      ...salesForm,
                                      discount_type: e.target.value,
                                    })
                                  }
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="rupiah">Rupiah (Rp)</option>
                                  <option value="percent">Persen (%)</option>
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs text-gray-600 mb-1">
                                  Nilai Diskon{" "}
                                  {salesForm.discount_type === "percent"
                                    ? "(%)"
                                    : "(Rp)"}
                                </label>
                                <input
                                  type="number"
                                  value={salesForm.discount_value}
                                  onChange={(e) =>
                                    setSalesForm({
                                      ...salesForm,
                                      discount_value: e.target.value,
                                    })
                                  }
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                  placeholder={
                                    salesForm.discount_type === "percent"
                                      ? "Contoh: 10"
                                      : "Contoh: 50000"
                                  }
                                  min="0"
                                  max={
                                    salesForm.discount_type === "percent"
                                      ? "100"
                                      : undefined
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          {/* Ongkir Section */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                              🚚 Biaya Pengiriman (Optional)
                            </label>
                            <input
                              type="number"
                              value={salesForm.shipping_cost}
                              onChange={(e) =>
                                setSalesForm({
                                  ...salesForm,
                                  shipping_cost: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder="Contoh: 15000"
                              min="0"
                            />
                          </div>

                          {/* Total Breakdown */}
                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="space-y-2">
                              {/* Subtotal */}
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-700">Subtotal:</span>
                                <span className="font-semibold text-gray-900">
                                  {formatRupiah(calculateTotalSales().subtotal)}
                                </span>
                              </div>

                              {/* Diskon */}
                              {calculateTotalSales().discountAmount > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-red-600">
                                    Diskon{" "}
                                    {salesForm.discount_type === "percent"
                                      ? `(${salesForm.discount_value}%)`
                                      : ""}
                                    :
                                  </span>
                                  <span className="font-semibold text-red-600">
                                    -{" "}
                                    {formatRupiah(
                                      calculateTotalSales().discountAmount,
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Ongkir */}
                              {calculateTotalSales().shippingCost > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-700">Ongkir:</span>
                                  <span className="font-semibold text-gray-900">
                                    +{" "}
                                    {formatRupiah(
                                      calculateTotalSales().shippingCost,
                                    )}
                                  </span>
                                </div>
                              )}

                              {/* Divider */}
                              <div className="border-t border-blue-300 my-2"></div>

                              {/* Total Akhir */}
                              <div className="flex justify-between items-center">
                                <span className="text-lg font-bold text-gray-800">
                                  Total Bayar:
                                </span>
                                <span className="text-2xl font-bold text-blue-600">
                                  {formatRupiah(
                                    calculateTotalSales().finalTotal,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Catatan (Optional)
                        </label>
                        <textarea
                          value={salesForm.notes}
                          onChange={(e) =>
                            setSalesForm({
                              ...salesForm,
                              notes: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          rows="3"
                          placeholder="Tambahkan catatan jika diperlukan"
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-4 border-t">
                        <button
                          type="submit"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                          <Save className="w-4 h-4" />
                          Simpan Penjualan
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowSalesForm(false);
                            setSelectedItems([]);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                        >
                          <X className="w-4 h-4" />
                          Batal
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Sales Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tanggal
                          </th>
                          {user.role === "pusat" && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Cabang
                            </th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Total Penjualan
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Catatan
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sales.length === 0 ? (
                          <tr>
                            <td
                              colSpan={user.role === "pusat" ? 4 : 3}
                              className="px-6 py-12 text-center"
                            >
                              <div className="flex flex-col items-center justify-center text-gray-500">
                                <ShoppingCart className="w-16 h-16 mb-3 text-gray-300" />
                                <p className="text-lg font-medium">
                                  Belum ada penjualan
                                </p>
                                <p className="text-sm mt-1">
                                  Pada periode{" "}
                                  {formatDate(salesDateFilter.startDate)} -{" "}
                                  {formatDate(salesDateFilter.endDate)}
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          sales.map((sale) => (
                            <tr key={sale.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                                {formatDate(sale.sale_date)}
                              </td>
                              {user.role === "pusat" && (
                                <td className="px-6 py-4 whitespace-nowrap text-gray-600 capitalize">
                                  {sale.branch.replace("kota", " Kota")}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                                {formatRupiah(sale.total_amount)}
                              </td>
                              <td className="px-6 py-4 text-gray-600">
                                {sale.notes || "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {/* Reports Tab - Khusus Cabang */}
            {activeTab === "reports" && user.role === "cabang" && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    📊 Laporan Stok Cabang {user.branch}
                  </h3>
                  <button
                    onClick={() => window.print()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    <Package className="w-4 h-4" />
                    Print Laporan
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          No
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Nama Buah
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Stok
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Satuan
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Harga
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Hari ke-
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Kadaluarsa
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredInventory().length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="px-4 py-8 text-center text-gray-500"
                          >
                            Belum ada stok buah
                          </td>
                        </tr>
                      ) : (
                        getFilteredInventory().map((item, idx) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                              {item.fruit_name}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap font-semibold text-blue-600">
                              {item.stock}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                              {item.unit}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                              {formatRupiah(item.price)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`font-semibold ${
                                  (item.days_in_warehouse || 0) >= 6
                                    ? "text-red-600"
                                    : (item.days_in_warehouse || 0) >= 4
                                      ? "text-yellow-600"
                                      : "text-green-600"
                                }`}
                              >
                                {item.days_in_warehouse || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-sm">
                              {formatDate(item.expiry_date)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}
                              >
                                {getStatusText(item.status)}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Ringkasan Stok:</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Total Kg</p>
                      <p className="text-xl font-bold text-green-600">
                        {getFilteredInventory()
                          .filter((i) => i.unit === "kg")
                          .reduce((sum, i) => sum + i.stock, 0)}{" "}
                        kg
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Dus</p>
                      <p className="text-xl font-bold text-blue-600">
                        {getFilteredInventory()
                          .filter((i) => i.unit === "dus")
                          .reduce((sum, i) => sum + i.stock, 0)}{" "}
                        dus
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Krat</p>
                      <p className="text-xl font-bold text-purple-600">
                        {getFilteredInventory()
                          .filter((i) => i.unit === "krat")
                          .reduce((sum, i) => sum + i.stock, 0)}{" "}
                        krat
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
